import React, { useEffect, useState } from 'react';
import { Col, Row, Spinner, Button } from 'react-bootstrap';
import { Field, getIn, useFormikContext } from 'formik';
import { useDispatch, useSelector } from 'react-redux';
import camelCaseRecursive from 'camelcase-keys-recursive';
import moment from 'moment';
import AvancedFilerInput from './Form/AvancedFilterInput';
import { DualList, FormikSelect } from '../../../components';
import { indexEmployeesRequest } from '../../../requests/employees';
import { debounceIndexConceptRequest } from '../../../requests/balances';
import es from '../../DataLoad/FormOptions';
import EmployeeBalancesTable from './Form/EmployeeBalancesTable';
import { getEmployeeBalances, payrollSettlementRequest } from '../../../requests/payrollSettlements';
import { clearSettlements } from './helpers/helpers';
import { handleDateDiff } from '../helpers';

const balanceTypes = [
  { label: 'Haberes', value: 'asset' },
  { label: 'Descuentos', value: 'discount' }
];

const settlementBalanceOptions = [
  { label: 'Indemnización Feriado', value: 'holiday_compensation' },
  { label: 'Remuneraciones Pendientes', value: 'pending_remuneration' },
  { label: 'Indemnización Sustitutiva de Aviso Previo', value: 'prior_notice_compensation' },
  { label: 'Indemnización Años de Servicio', value: 'compensation_years_service' }
];

const SettlementGroupBalances = ({ history }) => {
  const dispatch = useDispatch();
  const { values, errors, touched, setFieldValue, submitForm, isSubmitting } = useFormikContext();
  const { settlementGroup } = values;
  const [allBalances, setAllBalances] = useState([]);
  const [listBalances, setListBalances] = useState([]);
  const [balanceType, setBalanceType] = useState(null);
  const [showSpinner, setShowSpinner] = useState(false);
  const [settlementBase, setSettlementBase] = useState(null);
  const { currentCompany } = useSelector(state => state.auth);
  const employees = getIn(values, 'settlementGroup[employees]') || [];
  const formEndDate = getIn(values, 'settlementGroup[endDate]');

  const fetchEmployees = async params => {
    setShowSpinner(true);
    const serializedEmployees = await new Promise(resolve => {
      indexEmployeesRequest({
        params: {
          ...params,
          is_dt: false,
          sort_column: 'name',
          paginate: false,
          settlement: true
        },
        dispatch,
        successCallback: response => {
          clearSettlements(values);
          const employeesData = response.data.data;
          const fullEmployees = employeesData.map(employee => {
            return {
              ...employee,
              branchOffices: employee.branch_offices.map(branch => ({ label: branch.name, value: branch.id })),
              currentContract: employee?.correlative_identification,
              currentContractId: employee.contract_id,
              costCenter: employee?.cost_center?.name,
              jobManagement: employee?.job_management?.name,
              subsidiary: employee?.subsidiary?.name,
              startDate: moment(employee.start_date, 'YYYY-MM-DD').format('DD/MM/YYYY'),
              workedTime: handleDateDiff(
                moment(employee.start_date, 'YYYY-MM-DD').format('DD/MM/YYYY'),
                moment(formEndDate, 'DD/MM/YYYY').format('DD/MM/YYYY')
              )
            };
          });
          resolve(fullEmployees);
        }
      });
    });

    const vacationDataEmp = await new Promise(resolve => {
      const parsedEndDate = moment(settlementGroup.endDate, 'DD-MM-YYYY').format('YYYY/MM/DD');
      payrollSettlementRequest({
        dispatch,
        params: {
          filter_by_company: currentCompany.id,
          filter_employees: [...serializedEmployees.map(employee => employee.id)],
          month_date: parsedEndDate,
          year_date: parsedEndDate,
          settlement_data: true
        },
        successCallback: ({ data }) => {
          const convertedData = camelCaseRecursive(data);
          const { employeeData } = convertedData;
          resolve(employeeData);
        }
      });
    });

    const ids = [];
    const newEmployees = serializedEmployees.map(employee => {
      const vacationData = vacationDataEmp.vacationData.find(vacData => vacData.employeeId === employee.id);
      ids.push(employee.id);
      return {
        ...employee,
        proportionalVacationDays: vacationData?.proportionalVacation || 0,
        legalVacationDays: vacationData?.legalVacation || 0,
        aditionalVacationDays: vacationData?.additionalVacation || 0,
        progressiveVacationDays: vacationData?.progressiveVacation || 0
      };
    });
    setFieldValue('settlementGroup[employees]', camelCaseRecursive(newEmployees));
    setFieldValue('settlementGroup[employeeIds]', ids);
  };

  const fetchBalances = () => {
    debounceIndexConceptRequest({
      dispatch,
      params: {
        payroll_params: true,
        show_payroll: ''
      },
      successCallback: response => {
        const { data } = response;
        setAllBalances(data);
      }
    });
  };

  const filterBalances = () => {
    const filteredBalances = allBalances.filter(balance => balance.label === balanceType);
    if (balanceType === 'Haber' && settlementBase && settlementBase !== 'pending_remuneration') {
      const filteredBalanceBySettlementBase = filteredBalances.map(balanceObject => {
        return {
          ...balanceObject,
          options: balanceObject.options.filter(balance => balance[settlementBase])
        };
      });
      setListBalances(filteredBalanceBySettlementBase);
      return;
    }
    if (balanceType === 'Haber' && !settlementBase) {
      setListBalances([]);
      return;
    }
    setListBalances(filteredBalances);
  };

  const getBalanceAmount = (balanceId, payrollBalances, employeeID) => {
    const employeePayrollBalance = payrollBalances.find(
      _balance => _balance?.balance?.id === balanceId && _balance.employee.id === employeeID
    );
    const currentEmployee = employees.find(employee => employee.id === employeeID);
    const balance = currentEmployee?.balances?.find(_balance => _balance?.balance?.id === balanceId);
    if (employeePayrollBalance && !balance) {
      return {
        amount: employeePayrollBalance.amount,
        settlementBalanceId: null
      };
    }
    if (balance) {
      return {
        amount: balance.amount,
        settlementBalanceId: balance.settlementBalanceId || null,
        settlementBase: balance.settlementBase || settlementBase
      };
    }
    return {
      amount: 0,
      settlementBalanceId: null
    };
  };

  const balancesPerEmployee = (selectedValues, payrollBalances, employeeID) => {
    const mappedBalances = selectedValues.map(balanceId => {
      const [discounts, assets] = allBalances;
      const allBalanceOptions = [...discounts.options, ...assets.options];
      return allBalanceOptions.find(balance => balance.value === balanceId);
    });
    const discounts = mappedBalances.filter(balance => balance.type === 'discount');
    const assets = mappedBalances.filter(balance => balance.type === 'asset');
    const employeeBalances = [...assets, ...discounts].map(balance => {
      return {
        balance: { ...balance },
        label: balance.label,
        settlementBase: balance.type === 'discount' ? null : settlementBase,
        ...getBalanceAmount(balance.value, payrollBalances, employeeID)
      };
    });
    return employeeBalances;
  };

  const deleteEmployee = employee => {
    const newEmployees = employees.map(_employee => ({
      ..._employee,
      destroy: _employee.destroy ? _employee.destroy : _employee.id === employee.id
    }));
    setFieldValue('settlementGroup[employees]', newEmployees);
    setFieldValue(
      'settlementGroup[employeeIds]',
      newEmployees.map(_employee => _employee.id)
    );
  };

  const handleBalanceChange = selectedValues => {
    if (settlementGroup?.employeeIds.length === 0) return;

    if (allBalances.length === 0) return;

    setFieldValue('settlementGroup[balanceIds]', selectedValues);
    setShowSpinner(true);
    const dateToSearch = moment(values.settlementGroup.endDate, 'DD-MM-YYYY').subtract(1, 'months');
    const year = +dateToSearch.format('YYYY');
    const month = +dateToSearch.format('MM') - 1;
    getEmployeeBalances({
      dispatch,
      params: {
        filter_employee_ids: employees.filter(employee => !employee.destroy).map(_ => _.id) || [],
        filter_balance_ids: selectedValues,
        filter_active: true,
        payroll_process_date: `${year}-${month}`
      },
      successCallback: ({ data }) => {
        setShowSpinner(false);
        const newEmployees = employees.map(employee => {
          return {
            ...employee,
            balances: balancesPerEmployee(selectedValues, data.data, employee.id)
          };
        });
        setFieldValue('settlementGroup[employees]', newEmployees);
      }
    });
  };

  useEffect(fetchBalances, []);

  return (
    <Col>
      <Row>
        <AvancedFilerInput setEmployees={() => {}} fetchEmployees={fetchEmployees} />
        <Col xl={6} xs={12} md={6} lg={6}>
          <Field>
            {({ field }) => (
              <FormikSelect
                {...field}
                abbr
                label="Tipo de Conceptos"
                placeholder="Seleccione tipo de conceptos"
                options={balanceTypes}
                onChange={data => {
                  setBalanceType(data.value);
                  filterBalances();
                }}
              />
            )}
          </Field>
        </Col>
      </Row>
      {balanceType === 'Haber' && (
        <Row>
          <Col xl={6} xs={12} md={6} lg={6}>
            <Field>
              {({ field }) => (
                <FormikSelect
                  {...field}
                  abbr
                  label="Base de calculo"
                  placeholder="Seleccione base de calculo"
                  options={settlementBalanceOptions}
                  onChange={data => {
                    setSettlementBase(data.value);
                  }}
                />
              )}
            </Field>
          </Col>
        </Row>
      )}
      <Row>
        <Col className="mt-2" xs={12}>
          <DualList
            es={es}
            filterPlaceholder="Buscar Conceptos..."
            options={listBalances}
            selected={values.settlementGroup.balanceIds}
            name="settlementGroup[balanceIds]"
            onChange={selected => {
              setFieldValue('settlementGroup[balanceIds]', selected);
              handleBalanceChange(selected);
            }}
            error={getIn(errors, 'settlementGroup[balanceIds]')}
            touched={getIn(touched, 'settlementGroup[balanceIds]')}
          />
        </Col>
      </Row>
      <Row>
        {showSpinner ? (
          <Col className="div-content mt-5 mb-5">
            <div className="containerSpinnerLoadDash" style={{ position: 'absolute', height: '100%' }}>
              <Spinner animation="border" variant="primary" />
            </div>
          </Col>
        ) : (
          <Col className="mt-5">
            <EmployeeBalancesTable
              employees={employees}
              setFieldValue={setFieldValue}
              errors={errors}
              touched={touched}
              deleteEmployee={deleteEmployee}
            />
          </Col>
        )}
      </Row>
      <Row className="d-flex justify-content-end my-1">
        <Col md={2} xl={2} lg={2} xs={12} className="pb-2">
          <Button
            block
            variant="outline-info"
            disabled={isSubmitting}
            onClick={() => {
              history.push('/settlement_simulator');
            }}
          >
            Cancelar
          </Button>
        </Col>
        <Col md={2} xl={2} lg={2} xs={12}>
          <Button block variant="primary" disabled={isSubmitting} onClick={() => submitForm()}>
            Continuar
          </Button>
        </Col>
      </Row>
    </Col>
  );
};

export default SettlementGroupBalances;
